/**
 * 登录日志管理页面
 * 支持表格视图/时间线视图、高级搜索、统计图表、导出、详情抽屉
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Input, Form, Select, Tag, Row, Col,
  Statistic, Drawer, Descriptions, Timeline, DatePicker, Segmented,
  message, Tooltip, Pagination, Progress,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, DownloadOutlined,
  UnorderedListOutlined, FieldTimeOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  AppstoreOutlined, UserOutlined,
  EyeOutlined, BarChartOutlined,
  DesktopOutlined, MobileOutlined, TabletOutlined,
  WarningOutlined, SafetyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getLoginLogList, getLoginStatistics, exportLoginLogs } from '../../api/logs';
import type {
  LoginLogInfo,
  LoginLogQueryParams,
  LoginStatisticsResp,
  LoginResultStat,
  LoginTypeStat,
  DeviceTypeStat,
} from '../../api/types';
import { LoginType, LoginResult, DeviceType } from '../../api/types';

const { RangePicker } = DatePicker;

/** 登录方式中文映射 */
const loginTypeLabelMap: Record<string, string> = {
  [LoginType.PASSWORD]: '密码登录',
  [LoginType.SMS]: '短信登录',
  [LoginType.EMAIL]: '邮箱登录',
  [LoginType.WECHAT]: '微信登录',
  [LoginType.DINGTALK]: '钉钉登录',
  [LoginType.SSO]: 'SSO单点登录',
  [LoginType.QRCODE]: '扫码登录',
};

/** 登录方式颜色映射 */
const loginTypeColorMap: Record<string, string> = {
  [LoginType.PASSWORD]: 'blue',
  [LoginType.SMS]: 'cyan',
  [LoginType.EMAIL]: 'geekblue',
  [LoginType.WECHAT]: 'green',
  [LoginType.DINGTALK]: 'blue',
  [LoginType.SSO]: 'purple',
  [LoginType.QRCODE]: 'magenta',
};

/** 登录结果中文映射 */
const loginResultLabelMap: Record<string, string> = {
  [LoginResult.SUCCESS]: '成功',
  [LoginResult.FAIL_PASSWORD]: '密码错误',
  [LoginResult.FAIL_LOCKED]: '账号锁定',
  [LoginResult.FAIL_DISABLED]: '账号禁用',
  [LoginResult.FAIL_EXPIRED]: '账号过期',
  [LoginResult.FAIL_CAPTCHA]: '验证码错误',
  [LoginResult.FAIL_NOTFOUND]: '用户不存在',
};

/** 登录结果颜色映射 */
const loginResultColorMap: Record<string, string> = {
  [LoginResult.SUCCESS]: 'success',
  [LoginResult.FAIL_PASSWORD]: 'error',
  [LoginResult.FAIL_LOCKED]: 'warning',
  [LoginResult.FAIL_DISABLED]: 'error',
  [LoginResult.FAIL_EXPIRED]: 'warning',
  [LoginResult.FAIL_CAPTCHA]: 'error',
  [LoginResult.FAIL_NOTFOUND]: 'error',
};

/** 设备类型中文映射 */
const deviceTypeLabelMap: Record<string, string> = {
  [DeviceType.PC]: 'PC',
  [DeviceType.MOBILE]: '手机',
  [DeviceType.TABLET]: '平板',
  [DeviceType.UNKNOWN]: '未知',
};

/** 设备类型图标映射 */
const deviceTypeIconMap: Record<string, React.ReactNode> = {
  [DeviceType.PC]: <DesktopOutlined />,
  [DeviceType.MOBILE]: <MobileOutlined />,
  [DeviceType.TABLET]: <TabletOutlined />,
  [DeviceType.UNKNOWN]: <DesktopOutlined />,
};

/** 设备类型颜色映射 */
const deviceTypeColorMap: Record<string, string> = {
  [DeviceType.PC]: 'cyan',
  [DeviceType.MOBILE]: 'blue',
  [DeviceType.TABLET]: 'geekblue',
  [DeviceType.UNKNOWN]: 'default',
};

/** 风险等级中文映射 */
const riskLevelLabelMap: Record<number, string> = {
  0: '正常',
  1: '低风险',
  2: '中风险',
  3: '高风险',
};

/** 风险等级颜色映射 */
const riskLevelColorMap: Record<number, string> = {
  0: 'default',
  1: 'warning',
  2: 'orange',
  3: 'error',
};

/** 风险因素中文映射 */
const riskFactorLabelMap: Record<string, string> = {
  new_ip: '新 IP',
  new_location: '异地登录',
  brute_force: '暴力破解嫌疑',
  off_hours: '非工作时间',
};

/* ===== Mini Bar Chart Component ===== */
const MiniBarChart: React.FC<{
  data: { label: string; value: number; color?: string }[];
  maxItems?: number;
}> = ({ data, maxItems = 5 }) => {
  const sorted = [...data].sort((a, b) => b.value - a.value).slice(0, maxItems);
  const maxValue = sorted[0]?.value || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {sorted.map((item) => (
        <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 70, fontSize: 12, color: '#666', textAlign: 'right',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            {item.label}
          </span>
          <div style={{ flex: 1, background: '#f5f5f5', borderRadius: 2, height: 16, overflow: 'hidden' }}>
            <div style={{
              width: `${(item.value / maxValue) * 100}%`,
              height: '100%',
              background: item.color || '#1677ff',
              borderRadius: 2,
              minWidth: 2,
              transition: 'width 0.3s',
            }} />
          </div>
          <span style={{ width: 36, fontSize: 12, color: '#333', textAlign: 'right', flexShrink: 0 }}>
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

type ViewMode = 'table' | 'timeline';

const LoginLogList: React.FC = () => {
  const [list, setList] = useState<LoginLogInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [statistics, setStatistics] = useState<LoginStatisticsResp | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<LoginLogInfo | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showCharts, setShowCharts] = useState(false);
  const [searchForm] = Form.useForm();

  /** 构建查询参数 */
  const buildQueryParams = useCallback((): LoginLogQueryParams => {
    const formValues = searchForm.getFieldsValue();
    const params: LoginLogQueryParams = {
      page: pagination.current,
      page_size: pagination.pageSize,
    };

    if (formValues.username) {
      params.username = formValues.username;
    }
    if (formValues.login_type) {
      params.login_type = formValues.login_type;
    }
    if (formValues.login_result) {
      params.login_result = formValues.login_result;
    }
    if (formValues.device_type) {
      params.device_type = formValues.device_type;
    }
    if (formValues.risk_level !== undefined && formValues.risk_level !== null) {
      params.risk_level = formValues.risk_level;
    }
    if (formValues.timeRange && formValues.timeRange.length === 2) {
      params.start_time = formValues.timeRange[0].startOf('day').toISOString();
      params.end_time = formValues.timeRange[1].endOf('day').toISOString();
    }

    return params;
  }, [searchForm, pagination]);

  /** 获取登录日志列表 */
  const fetchLoginLogList = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildQueryParams();
      const res: any = await getLoginLogList(params);
      if (res?.data) {
        const data = res.data?.data || res.data;
        setList(data.list || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('获取登录日志列表失败', error);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  /** 获取统计数据 */
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const formValues = searchForm.getFieldsValue();
      const statsParams: { start_time?: string; end_time?: string } = {};
      if (formValues.timeRange && formValues.timeRange.length === 2) {
        statsParams.start_time = formValues.timeRange[0].startOf('day').toISOString();
        statsParams.end_time = formValues.timeRange[1].endOf('day').toISOString();
      }
      const res: any = await getLoginStatistics(statsParams);
      if (res?.data) {
        setStatistics(res.data?.data || res.data);
      }
    } catch (error) {
      console.error('获取统计数据失败', error);
    } finally {
      setStatsLoading(false);
    }
  }, [searchForm]);

  // 初始化加载
  useEffect(() => {
    fetchLoginLogList();
    fetchStatistics();
  }, [pagination.current, pagination.pageSize]);

  /** 搜索 */
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchLoginLogList();
    fetchStatistics();
  };

  /** 重置搜索 */
  const handleReset = () => {
    searchForm.resetFields();
    setPagination((prev) => ({ ...prev, current: 1 }));
    fetchLoginLogList();
    fetchStatistics();
  };

  /** 表格分页变更 */
  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  /** 打开详情抽屉 */
  const handleViewDetail = (record: LoginLogInfo) => {
    setCurrentRecord(record);
    setDrawerVisible(true);
  };

  /** 导出登录日志 */
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = buildQueryParams();
      const response = await exportLoginLogs(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `登录日志_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败', error);
      message.error('导出失败');
    } finally {
      setExportLoading(false);
    }
  };

  /** 成功率 */
  const successRate = statistics?.total_logins
    ? Math.round((statistics.success_logins / statistics.total_logins) * 100)
    : 0;

  /** 表格列定义 */
  const columns: ColumnsType<LoginLogInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 70,
      fixed: 'left',
    },
    {
      title: '用户名',
      dataIndex: 'username',
      width: 100,
      ellipsis: true,
    },
    {
      title: '登录方式',
      dataIndex: 'login_type',
      width: 100,
      render: (type: LoginType) => (
        <Tag color={loginTypeColorMap[type] || 'default'}>
          {loginTypeLabelMap[type] || type}
        </Tag>
      ),
    },
    {
      title: '登录结果',
      dataIndex: 'login_result',
      width: 100,
      render: (result: LoginResult) => (
        <Tag color={loginResultColorMap[result] || 'default'}>
          {loginResultLabelMap[result] || result}
        </Tag>
      ),
    },
    {
      title: '设备类型',
      dataIndex: 'device_type',
      width: 90,
      render: (type: DeviceType) => (
        <Tag icon={deviceTypeIconMap[type]} color={deviceTypeColorMap[type] || 'default'}>
          {deviceTypeLabelMap[type] || type || '未知'}
        </Tag>
      ),
    },
    {
      title: 'IP地址',
      dataIndex: 'login_ip',
      width: 130,
      render: (ip: string) => ip || '-',
    },
    {
      title: '归属地',
      dataIndex: 'login_location',
      width: 120,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          {text || '-'}
        </Tooltip>
      ),
    },
    {
      title: '浏览器',
      key: 'browser',
      width: 100,
      ellipsis: true,
      render: (_, record) => {
        const parts = [record.browser_name, record.os_name].filter(Boolean);
        const text = parts.join(' / ') || '-';
        return <Tooltip title={text}>{text}</Tooltip>;
      },
    },
    {
      title: '风险等级',
      dataIndex: 'risk_level',
      width: 90,
      render: (level: number) => {
        if (!level) return <span style={{ color: '#ccc' }}>&mdash;</span>;
        return (
          <Tag color={riskLevelColorMap[level] || 'default'}>
            {riskLevelLabelMap[level] || `Level ${level}`}
          </Tag>
        );
      },
    },
    {
      title: '登录时间',
      dataIndex: 'created_at',
      width: 170,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      render: (text: string) => {
        if (!text) return '-';
        const time = dayjs(text);
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{time.format('HH:mm:ss')}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{time.format('YYYY-MM-DD')}</div>
          </div>
        );
      },
    },
    {
      title: '操作',
      key: 'action_col',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(record)}
        >
          详情
        </Button>
      ),
    },
  ];

  /** 时间线视图渲染 */
  const renderTimeline = () => {
    if (!list.length) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
          暂无数据
        </div>
      );
    }

    return (
      <Timeline
        style={{ padding: '16px 0' }}
        items={list.map((item) => {
          const isSuccess = item.login_result === LoginResult.SUCCESS;
          const dotColor = item.risk_level >= 3 ? 'red' : item.risk_level >= 1 ? '#faad14' : isSuccess ? 'green' : 'red';

          return {
            key: item.id,
            color: dotColor,
            children: (
              <div style={{ paddingBottom: 8 }}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ color: '#999', fontSize: 12 }}>
                    {dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </span>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <strong>{item.username}</strong>
                  {' '}
                  <Tag color={loginTypeColorMap[item.login_type] || 'default'} style={{ marginRight: 4 }}>
                    {loginTypeLabelMap[item.login_type] || item.login_type}
                  </Tag>
                  {' '}
                  <Tag color={loginResultColorMap[item.login_result] || 'default'}>
                    {loginResultLabelMap[item.login_result] || item.login_result}
                  </Tag>
                  {item.risk_level > 0 && (
                    <>
                      {' '}
                      <Tag color={riskLevelColorMap[item.risk_level]}>
                        {riskLevelLabelMap[item.risk_level]}
                      </Tag>
                    </>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>
                  {item.device_type && (
                    <Tag
                      icon={deviceTypeIconMap[item.device_type]}
                      color={deviceTypeColorMap[item.device_type] || 'default'}
                      style={{ fontSize: 11 }}
                    >
                      {deviceTypeLabelMap[item.device_type] || item.device_type}
                    </Tag>
                  )}
                  {item.login_ip}
                  {item.login_location && ` | ${item.login_location}`}
                </div>
                {item.fail_reason && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 2 }}>
                    失败原因: {item.fail_reason}
                  </div>
                )}
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, marginTop: 2 }}
                  onClick={() => handleViewDetail(item)}
                >
                  查看详情
                </Button>
              </div>
            ),
          };
        })}
      />
    );
  };

  return (
    <div className="login-log-list-container">
      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="总登录次数"
              value={statistics?.total_logins ?? 0}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="成功率"
              value={successRate}
              suffix="%"
              valueStyle={{ color: successRate >= 90 ? '#3f8600' : successRate >= 70 ? '#faad14' : '#cf1322' }}
            />
            <Progress
              percent={successRate}
              size="small"
              showInfo={false}
              strokeColor={successRate >= 90 ? '#52c41a' : successRate >= 70 ? '#faad14' : '#ff4d4f'}
              style={{ marginTop: 4 }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="失败次数"
              value={statistics?.failed_logins ?? 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="风险事件"
              value={statistics?.risk_events ?? 0}
              valueStyle={{ color: (statistics?.risk_events ?? 0) > 0 ? '#fa8c16' : undefined }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card loading={statsLoading} size="small">
            <Statistic
              title="今日活跃用户"
              value={statistics?.today_users ?? 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 统计图表 */}
      {showCharts && statistics && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Card title="登录结果分布" size="small" loading={statsLoading}>
              {statistics.result_stats?.length ? (
                <MiniBarChart
                  data={statistics.result_stats.map((s: LoginResultStat) => ({
                    label: loginResultLabelMap[s.result] || s.result,
                    value: s.count,
                    color: s.result === LoginResult.SUCCESS ? '#52c41a' : '#ff4d4f',
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>暂无数据</div>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="登录方式分布" size="small" loading={statsLoading}>
              {statistics.type_stats?.length ? (
                <MiniBarChart
                  data={statistics.type_stats.map((s: LoginTypeStat) => ({
                    label: loginTypeLabelMap[s.login_type] || s.login_type,
                    value: s.count,
                    color: '#1677ff',
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>暂无数据</div>
              )}
            </Card>
          </Col>
          <Col span={8}>
            <Card title="设备类型分布" size="small" loading={statsLoading}>
              {statistics.device_stats?.length ? (
                <MiniBarChart
                  data={statistics.device_stats.map((s: DeviceTypeStat) => ({
                    label: deviceTypeLabelMap[s.device_type] || s.device_type,
                    value: s.count,
                    color: '#13c2c2',
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 16 }}>暂无数据</div>
              )}
            </Card>
          </Col>
        </Row>
      )}

      <Card title="登录日志" bordered={false}>
        {/* 高级搜索表单 */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16, flexWrap: 'wrap', gap: '8px 0' }}
        >
          <Form.Item name="username">
            <Input
              placeholder="用户名"
              allowClear
              style={{ width: 140 }}
            />
          </Form.Item>
          <Form.Item name="login_type">
            <Select
              placeholder="登录方式"
              style={{ width: 130 }}
              allowClear
              options={Object.entries(LoginType).map(([, value]) => ({
                value,
                label: loginTypeLabelMap[value] || value,
              }))}
            />
          </Form.Item>
          <Form.Item name="login_result">
            <Select
              placeholder="登录结果"
              style={{ width: 120 }}
              allowClear
              options={Object.entries(LoginResult).map(([, value]) => ({
                value,
                label: loginResultLabelMap[value] || value,
              }))}
            />
          </Form.Item>
          <Form.Item name="device_type">
            <Select
              placeholder="设备类型"
              style={{ width: 110 }}
              allowClear
              options={Object.entries(DeviceType).map(([, value]) => ({
                value,
                label: deviceTypeLabelMap[value] || value,
              }))}
            />
          </Form.Item>
          <Form.Item name="risk_level">
            <Select
              placeholder="风险等级"
              style={{ width: 110 }}
              allowClear
              options={[
                { value: 0, label: '正常' },
                { value: 1, label: '低风险' },
                { value: 2, label: '中风险' },
                { value: 3, label: '高风险' },
              ]}
            />
          </Form.Item>
          <Form.Item name="timeRange">
            <RangePicker
              style={{ width: 260 }}
              placeholder={['开始时间', '结束时间']}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 工具栏 */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Segmented
              value={viewMode}
              onChange={(val) => setViewMode(val as ViewMode)}
              options={[
                { label: '表格视图', value: 'table', icon: <UnorderedListOutlined /> },
                { label: '时间线视图', value: 'timeline', icon: <FieldTimeOutlined /> },
              ]}
            />
            <Button
              icon={<BarChartOutlined />}
              type={showCharts ? 'primary' : 'default'}
              ghost={showCharts}
              onClick={() => setShowCharts(!showCharts)}
            >
              {showCharts ? '收起图表' : '统计图表'}
            </Button>
          </Space>
          <Button
            icon={<DownloadOutlined />}
            loading={exportLoading}
            onClick={handleExport}
          >
            导出
          </Button>
        </div>

        {/* 表格视图 */}
        {viewMode === 'table' && (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={list}
            pagination={{
              ...pagination,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (t) => `共 ${t} 条`,
            }}
            loading={loading}
            onChange={handleTableChange}
            scroll={{ x: 1320 }}
            locale={{ emptyText: '暂无数据' }}
          />
        )}

        {/* 时间线视图 */}
        {viewMode === 'timeline' && (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                加载中...
              </div>
            ) : (
              renderTimeline()
            )}
            {/* 时间线分页 */}
            {total > 0 && (
              <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={total}
                  showSizeChanger
                  showQuickJumper
                  showTotal={(t) => `共 ${t} 条`}
                  onChange={(page, pageSize) => {
                    setPagination({ current: page, pageSize });
                  }}
                />
              </div>
            )}
          </>
        )}
      </Card>

      {/* 详情抽屉 */}
      <Drawer
        title="登录详情"
        placement="right"
        width={560}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false);
          setCurrentRecord(null);
        }}
      >
        {currentRecord && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* 基本信息 */}
            <div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f5f5f5' }}>
                基本信息
              </div>
              <Descriptions column={2} size="small" labelStyle={{ fontWeight: 500, width: 100, color: '#888' }}>
                <Descriptions.Item label="用户名"><strong>{currentRecord.username}</strong></Descriptions.Item>
                <Descriptions.Item label="登录时间">
                  {currentRecord.created_at ? dayjs(currentRecord.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="登录结果">
                  <Tag color={loginResultColorMap[currentRecord.login_result] || 'default'}>
                    {loginResultLabelMap[currentRecord.login_result] || currentRecord.login_result}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="登录方式">
                  {loginTypeLabelMap[currentRecord.login_type] || currentRecord.login_type || '-'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* 网络信息 */}
            <div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f5f5f5' }}>
                网络信息
              </div>
              <Descriptions column={2} size="small" labelStyle={{ fontWeight: 500, width: 100, color: '#888' }}>
                <Descriptions.Item label="IP 地址">
                  <span style={{ fontFamily: 'monospace' }}>{currentRecord.login_ip || '-'}</span>
                </Descriptions.Item>
                <Descriptions.Item label="归属地">{currentRecord.login_location || '-'}</Descriptions.Item>
              </Descriptions>
            </div>

            {/* 设备信息 */}
            <div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f5f5f5' }}>
                设备信息
              </div>
              <Descriptions column={2} size="small" labelStyle={{ fontWeight: 500, width: 100, color: '#888' }}>
                <Descriptions.Item label="设备类型">
                  {currentRecord.device_type ? (
                    <Tag icon={deviceTypeIconMap[currentRecord.device_type]} color={deviceTypeColorMap[currentRecord.device_type] || 'default'}>
                      {deviceTypeLabelMap[currentRecord.device_type] || currentRecord.device_type}
                    </Tag>
                  ) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="操作系统">{currentRecord.os_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="浏览器">{currentRecord.browser_name || '-'}</Descriptions.Item>
                <Descriptions.Item label="登录方法">{currentRecord.login_method || '-'}</Descriptions.Item>
                <Descriptions.Item label="User-Agent" span={2}>
                  <code style={{ background: '#f5f5f5', padding: '2px 8px', borderRadius: 4, fontSize: 12, wordBreak: 'break-all' }}>
                    {currentRecord.user_agent || '-'}
                  </code>
                </Descriptions.Item>
              </Descriptions>
            </div>

            {/* 会话信息 */}
            {currentRecord.session_id && (
              <div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f5f5f5' }}>
                  会话信息
                </div>
                <Descriptions column={2} size="small" labelStyle={{ fontWeight: 500, width: 100, color: '#888' }}>
                  <Descriptions.Item label="会话 ID">
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{currentRecord.session_id}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="登出时间">
                    {currentRecord.logout_time ? dayjs(currentRecord.logout_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="登出方式">{currentRecord.logout_type || '-'}</Descriptions.Item>
                  <Descriptions.Item label="会话时长">
                    {currentRecord.duration_secs != null
                      ? currentRecord.duration_secs >= 3600
                        ? `${Math.floor(currentRecord.duration_secs / 3600)} 小时 ${Math.floor((currentRecord.duration_secs % 3600) / 60)} 分钟`
                        : currentRecord.duration_secs >= 60
                          ? `${Math.floor(currentRecord.duration_secs / 60)} 分钟`
                          : `${currentRecord.duration_secs} 秒`
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}

            {/* 风险评估 */}
            {currentRecord.risk_level > 0 && (
              <div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f5f5f5' }}>
                  风险评估
                </div>
                <div style={{
                  background: currentRecord.risk_level >= 3 ? '#fde8e8' : currentRecord.risk_level >= 2 ? '#fff7ed' : '#fffbeb',
                  border: `1px solid ${currentRecord.risk_level >= 3 ? '#fca5a5' : currentRecord.risk_level >= 2 ? '#fed7aa' : '#fde68a'}`,
                  borderRadius: 8,
                  padding: 14,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Tag color={riskLevelColorMap[currentRecord.risk_level]}>
                      {riskLevelLabelMap[currentRecord.risk_level]}
                    </Tag>
                    <span style={{ fontSize: 13, color: '#92400e' }}>
                      评分 {currentRecord.risk_level}/3
                    </span>
                  </div>
                  {currentRecord.risk_factors && currentRecord.risk_factors.length > 0 && (
                    <div style={{ fontSize: 13, color: '#666' }}>
                      {currentRecord.risk_factors.map((factor) => (
                        <div key={factor} style={{ marginBottom: 4 }}>
                          <SafetyOutlined style={{ marginRight: 6, color: '#d97706' }} />
                          <strong>{riskFactorLabelMap[factor] || factor}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 失败原因 */}
            {currentRecord.fail_reason && (
              <div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #f5f5f5' }}>
                  失败信息
                </div>
                <div style={{ color: '#ff4d4f', fontSize: 13 }}>
                  {currentRecord.fail_reason}
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default LoginLogList;
